import type { SortField } from '~/stores/documents'
import type { DocumentStatusFilter, DocumentTypeFilter } from '~~/shared/items/documentQuery'

/**
 * The finder's columns, as DATA (AlexAmin/marcus#169).
 *
 * The header row and the row renderer read one definition instead of two parallel templates. That is
 * the whole reason this is a module and not markup: a column's label, its width and whether it sorts
 * are three facts that have to agree between the thing you click and the thing that lines up
 * underneath it, and every previous attempt in this area drifted precisely because they were spelled
 * twice (`/app/docs` and the rail each held their own copy of the source list before #158).
 *
 * The CELLS are still explicit markup in `BrowserItemList.vue`, deliberately. Five cells rendered by
 * a `v-for` and a switch is not simpler than five cells; it is the same code with the reader's job
 * made harder, and one of them (Nombre) carries the folder chevron, the drag handle and the status
 * chip. What is shared here is the column SET and its geometry, which is what has to match.
 */

/** Which columns can be filtered, and therefore which header carries a checkbox list. */
export type DocumentColumnFilter = 'type' | 'status'

export interface DocumentColumn {
  key: 'name' | 'type' | 'status' | 'homes' | 'modified'
  /** i18n key for the header label. */
  labelKey: string
  /** Tailwind classes fixing the cell's width. Applied identically to header and cell. */
  width: string
  /** Present when the header sorts. Absent means the column has no ordering the user has. */
  sortField?: SortField
  /** Present when the header filters. */
  filter?: DocumentColumnFilter
  /** Right-aligned columns (only the date, which reads as a quantity). */
  align?: 'right'
  /**
   * Hidden below this container width, in pixels. The table lives in a panel, not the viewport —
   * the rail is narrow at any window size — so the collapse is driven by a CONTAINER query, and
   * these are the breakpoints `BrowserItemList` maps to `@sm`/`@md`/`@lg` classes.
   */
  hideBelow?: 'sm' | 'md' | 'lg'
}

/**
 * ── WHY THERE IS NO "FUENTE" COLUMN, AND WHY "OTROS HOGARES" IS PLURAL ─────────────────────────
 *
 * A singular source column would have to name ONE canonical home per row, which is exactly the
 * one-home-wins rule AlexAmin/marcus#168 deleted — smuggled back in as a UI choice. `homes` is the
 * plural of it: the memberships NOT already stated by the band the row sits under. Empty is a real
 * and common value and renders as nothing, never as a dash.
 *
 * `status` does not sort. The ordering of a status enum is not a fact the user has — is `draft`
 * before or after `reviewed`? — so a sort arrow there would be an arbitrary order presented as a
 * meaningful one. It filters instead, which is the question people actually ask of it.
 *
 * `homes` does not sort or filter in this pass: both would need a server predicate over four link
 * tables, and neither is what the column is for. It exists to make the sixth identically-named
 * Convenio distinguishable, which is a reading task.
 */
/**
 * ── THE TWO FLUID COLUMNS SHARE THE LEFTOVER 2:1, AND NEITHER HAS A PERCENTAGE (marcus#190) ─────
 *
 * `name` and `homes` are the only columns without a fixed width; everything else — the checkbox, the
 * badge, Tipo, Estado, Modificado, the kebab — is `shrink-0` and costs a constant 388px of the
 * container at full column count. What is left over is split between these two, and HOW it is split
 * is the whole of this bug.
 *
 * `homes` used to be `w-[30%]`, which under flexbox is a BASIS, not a share: a basis is served in
 * full before any `flex-grow` is honoured. `name` was `flex-1` — basis `0`, grow `1` — so it was
 * paid only out of what survived homes' 30%. Measured on `/app/docs` at a 517px container (the
 * common case: docs rail plus a client panel open), that came to **`name` 0px and `homes` 129px** —
 * every row a type badge and no name at all. It was not a narrow edge either: name stayed below
 * homes until roughly a 1000px container, so the identity column was the smaller one at almost
 * every width the page is actually used at.
 *
 * Both are now basis-`0` with a grow RATIO, so the leftover is divided 2:1 in Nombre's favour at
 * every width and `name` cannot reach zero unless the leftover itself is zero. Measured after:
 * 83/42 at 513px, 208/104 at 700px, 675/337 at 1400px, no horizontal overflow at any width.
 *
 * **A `min-w` floor on `name` would be the wrong shape of fix.** A fixed px floor is a promise the
 * row cannot keep once the leftover drops below it — the floor wins, the shrinkable cell hits zero,
 * and the overflow is pushed onto the kebab instead. The ratio degrades instead of breaking, which
 * is why there is no floor here. `min-w-0` stays on both, and on `name` it is load-bearing for a
 * different reason: without it `min-width: auto` would stop `truncate` ellipsizing a long file name.
 *
 * Below the `@lg` breakpoint `homes` hides and `name` takes the leftover whole, exactly as the
 * ladder in `hideBelow` intends — this change cooperates with that ladder rather than replacing it.
 */
export const DOCUMENT_COLUMNS: readonly DocumentColumn[] = [
  { key: 'name', labelKey: 'documents.columnName', width: 'flex-2 min-w-0', sortField: 'name' },
  { key: 'type', labelKey: 'documents.columnType', width: 'w-16 shrink-0', sortField: 'type', filter: 'type', hideBelow: 'sm' },
  { key: 'status', labelKey: 'documents.columnStatus', width: 'w-24 shrink-0', filter: 'status', hideBelow: 'md' },
  { key: 'homes', labelKey: 'documents.columnHomes', width: 'flex-1 min-w-0', hideBelow: 'lg' },
  { key: 'modified', labelKey: 'documents.columnModified', width: 'w-20 shrink-0', sortField: 'date', align: 'right' },
] as const

/** The container-query class that hides a column below its breakpoint. One place, so the header and
 *  the cell can never disagree about when a column disappears. */
export function columnVisibility(column: DocumentColumn): string {
  if (column.hideBelow === 'sm') return 'hidden @sm:flex'
  if (column.hideBelow === 'md') return 'hidden @md:flex'
  if (column.hideBelow === 'lg') return 'hidden @lg:flex'
  return 'flex'
}

/** The checkbox lists a filtering header offers. Values are the wire values; labels are i18n keys. */
export const TYPE_FILTER_OPTIONS: readonly { value: DocumentTypeFilter; labelKey: string }[] = [
  { value: 'pdf', labelKey: 'documents.filterTypePdf' },
  { value: 'docx', labelKey: 'documents.filterTypeDocx' },
  { value: 'image', labelKey: 'documents.filterImages' },
] as const

export const STATUS_FILTER_OPTIONS: readonly { value: DocumentStatusFilter; labelKey: string }[] = [
  { value: 'draft', labelKey: 'documents.statusDraft' },
  { value: 'reviewed', labelKey: 'documents.statusReviewed' },
  { value: 'final', labelKey: 'documents.statusFinal' },
] as const
